import { Form, Formik } from "formik";
import { array, object, string } from "yup";
import moment from "moment/moment";
import { useState } from "react";

// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import { dateMax } from "../../models/utils";
// @ts-expect-error pure js
import DocumentDownloader from "../UI/DocumentDownloader";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
import Text from "../UI/Text";
import ModalDialog, { ModalDialogFooter } from "../UI/ModalDialog";
import Callout from "../UI/Callout";

interface ExportFecFormProps {
  profileBailleurId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FecJournal = "ventes" | "paiements" | "achats";

interface ExportFecFormValues {
  debut: string;
  fin: string;
  journals: FecJournal[];
}

const JOURNAL_OPTIONS: {
  value: FecJournal;
  label: string;
  description: string;
}[] = [
  {
    value: "ventes",
    label: "Ventes",
    description: "Appels émis aux locataires (loyers, charges, DG…)",
  },
  {
    value: "paiements",
    label: "Paiements locataires et bailleur",
    description: "Encaissements locataires et reversements au bailleur",
  },
  {
    value: "achats",
    label: "Charges et travaux côté bailleur",
    description: "Charges déductibles, travaux, CCA et financements",
  },
];

export default function ExportFecForm({
  profileBailleurId,
  open,
  onOpenChange,
}: ExportFecFormProps) {
  const axios = useAuthenticatedAxios();
  const [error, setError] = useState<string>("");

  async function generateSignedUrl(
    values: ExportFecFormValues,
    setSignedUrl: (url: string) => void,
  ) {
    try {
      const response = await axios.post(
        `/profiles/${profileBailleurId}/export-fec`,
        values,
      );
      setSignedUrl(response.data);
    } catch (err) {
      setError(handleAPIError(err));
    }
  }

  const initialValues: ExportFecFormValues = {
    debut: moment().startOf("year").subtract(1, "year").format("YYYY-MM-DD"),
    fin: moment().endOf("year").subtract(1, "year").format("YYYY-MM-DD"),
    journals: ["ventes"],
  };

  return (
    <ModalDialog
      size="sm"
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setError("");
        onOpenChange(nextOpen);
      }}
      header="Export FEC"
    >
      <DocumentDownloader>
        {(setSignedUrl: (url: string) => void) => (
          <Formik
            onSubmit={(values: ExportFecFormValues) =>
              generateSignedUrl(values, setSignedUrl)
            }
            initialValues={initialValues}
            enableReinitialize
            validationSchema={object({
              debut: dateMax().required(
                "La date de début de la période est obligatoire",
              ),
              fin: dateMax().required(
                "La date de fin de la période est obligatoire",
              ),
              journals: array()
                .of(string())
                .min(1, "Sélectionnez au moins un journal à exporter"),
            })}
          >
            {({ values, errors, touched }) => (
              <Form>
                <Text className="marginB10">
                  Génère un fichier FEC (Fichier des Écritures Comptables,
                  format DGFIP) pour la période sélectionnée.
                </Text>
                <TextInput
                  type="date"
                  name="debut"
                  label={"Début de la période"}
                />
                <TextInput type="date" name="fin" label={"Fin de la période"} />
                <Text weight="medium" className="marginT10">
                  Journaux à inclure
                </Text>
                {JOURNAL_OPTIONS.map((option) => (
                  <Checkbox
                    key={option.value}
                    name="journals"
                    value={option.value}
                  >
                    <Text as="span" weight="medium">
                      {option.label}
                    </Text>
                    <Text as="span" variant="deci" color="muted">
                      {" "}
                      — {option.description}
                    </Text>
                  </Checkbox>
                ))}
                {touched.journals && typeof errors.journals === "string" && (
                  <Text color="danger" variant="deci">
                    {errors.journals}
                  </Text>
                )}
                <Callout variant="danger">{error}</Callout>
                <ModalDialogFooter>
                  <FormikSubmitButton disabled={values.journals.length === 0}>
                    Télécharger
                  </FormikSubmitButton>
                </ModalDialogFooter>
              </Form>
            )}
          </Formik>
        )}
      </DocumentDownloader>
    </ModalDialog>
  );
}
